import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { PasswordStrengthValidator } from '../../../bookkeeping/validators/password-strength-validator';
import { UserValidator } from 'src/app/data/validator/user-validator';
import { RegisterService } from 'src/app/data/service/register.service';
import { AuthService } from 'src/app/core/service/auth/auth.service';
import { UserService } from 'src/app/data/service/user.service';
import { NotificationsHubService } from 'src/app/core/service/hubs/notifications-hub.service';
import { HttpInternalService } from 'src/app/data/service/http.service';
import { AnalyticsService } from 'src/app/core/service/analytics/analytics.service';
import { ManageIntercom } from 'src/app/core/util/manage-intercom';
import { countries } from 'src/app/data/data/countries';
import { Country } from 'src/app/data/model/country';
import { AppConfigService } from 'src/app/appConfig.service';
import { OrganizationService } from 'src/app/data/service/organization.service';

declare const window: any;

// Where most signups come from, so these sit above the alphabetical list, in this order.
const COMMON_COUNTRIES = ['Australia', 'United Kingdom', 'United States', 'New Zealand', 'South Africa'];

// SD-1137: Consolidated signup step that replaces step-one / step-two / step-two-company.
// One form, one submit, one PostHog event. Pre-fills the Country picker from IP.
@Component({
  selector: 'app-step-create-account',
  templateUrl: './step-create-account.component.html',
  styleUrls: ['./step-create-account.component.scss']
})
export class StepCreateAccountComponent implements OnInit, AfterViewInit, OnDestroy {
  form: FormGroup;
  isWarningShown = false;
  loading = false;
  countryList: Country[] = [
    ...COMMON_COUNTRIES.map(name => countries.find(c => c.name === name)).filter((c): c is Country => !!c),
    ...countries.filter(c => COMMON_COUNTRIES.indexOf(c.name) === -1)
  ];
  countryGroup = (c: Country): string =>
    COMMON_COUNTRIES.indexOf(c.name) !== -1 ? 'Common' : 'All countries';
  private get baseUrl(): string {
    return this.configService.apiURL;
  }
  // SD-1165 Cloudflare Turnstile. Rendered explicitly in ngAfterViewInit so the
  // widget mounts reliably across SPA route transitions (auto-render's DOM
  // scan can miss elements added after api.js loads).
  // turnstileSiteKey = environment.turnstileSiteKey;
  turnstileToken = '';
  turnstileError = false;
  turnstileBlocked = false; // api.js never loaded (adblock / network)
  @ViewChild('turnstileContainer') turnstileContainer: ElementRef<HTMLDivElement>;
  private turnstileWidgetId: string | undefined;
  private turnstileRenderTimer: any;

  // SD-1137: progressive loader so the ~5-10s wait doesn't feel like a hung button.
  // Each phase advances on a timer. If the backend finishes before we reach the last
  // phase, remaining phases tick through quickly. If the backend is still pending
  // when we reach the last phase, it keeps pulsing until the response lands.
  loadingPhases: { title: string; desc: string; meta: string }[] = [
    { title: 'Creating your workspace',  desc: 'Setting up your company and inviting you in.',       meta: 'SYS' },
    { title: 'Securing your account',    desc: 'Creating your login and protecting your details.',   meta: 'AUTH' },
    { title: 'Applying your settings',   desc: 'Setting your country and default currency.',         meta: 'SETTINGS' },
    { title: 'Preparing your dashboard', desc: 'Getting everything ready for your first visit.',     meta: 'DATA' },
    { title: 'Finishing up',             desc: 'Signing you in and taking you to Synced.',           meta: 'DONE' }
  ];
  currentPhase = 0;
  get loadingStepLabel(): string {
    const shown = Math.min(this.currentPhase + 1, this.loadingPhases.length);
    return `Setting up · step ${shown} of ${this.loadingPhases.length}`;
  }
  showPassword = false;
  passwordFocused = false;

  // Password strength evaluation — shown as four pills below the field.
  private get pwd(): string {
    return (this.form?.get('password')?.value || '') as string;
  }
  get pwdHasLength():   boolean { return this.pwd.length >= 8; }
  get pwdHasUppercase():boolean { return /[A-Z]/.test(this.pwd); }
  get pwdHasNumber():   boolean { return /[0-9]/.test(this.pwd); }
  get pwdHasSymbol():   boolean { return /[^A-Za-z0-9]/.test(this.pwd); }
  get showPwdHints():   boolean { return this.passwordFocused || this.pwd.length > 0; }
  private phaseTimer: any;
  private backendDone = false;
  private pendingNavigate: (() => void) | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private userValidator: UserValidator,
    private registerService: RegisterService,
    private authService: AuthService,
    private userService: UserService,
    private notifications: NotificationsHubService,
    private http: HttpInternalService,
    private analytics: AnalyticsService,
    private ngZone: NgZone,
    private configService: AppConfigService,
    private organizationService: OrganizationService
  ) {}

  get f() { return this.form.controls; }
  private get turnstileSiteKey(): string {
    return this.configService.turnstileSiteKey;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email],
        [this.userValidator.uniqueEmailValidator('Account/CheckEmail')]],
      password: ['', [Validators.required, PasswordStrengthValidator]],
      firstname: ['', [Validators.required]],
      lastname: ['', [Validators.required]],
      companyname: ['', [Validators.required]],
      country: [null, [Validators.required]],
      accountRemember: [true, [Validators.requiredTrue]]
    });

    this.analytics.capture('signup_view', { step: 'create_account' });
    this.detectCountry();
  }

  ngAfterViewInit(): void {
    // SD-1165: explicit render — see field comment for rationale.
    this.renderTurnstile();
  }

  // Polls for window.turnstile (api.js is async/defer) up to ~10s, then gives
  // up and surfaces a "verification couldn't load" state. Callbacks re-enter
  // NgZone because the widget fires them from its sandboxed iframe context.
  private renderTurnstile(attemptsLeft = 50): void {
    if (!this.turnstileContainer) { return; }
    if (this.turnstileWidgetId !== undefined) { return; }
    const ts = window.turnstile;
    if (!ts || typeof ts.render !== 'function') {
      if (attemptsLeft <= 0) {
        this.ngZone.run(() => { this.turnstileBlocked = true; });
        return;
      }
      this.turnstileRenderTimer = setTimeout(
        () => this.renderTurnstile(attemptsLeft - 1), 200
      );
      return;
    }
    try {
      this.turnstileWidgetId = ts.render(this.turnstileContainer.nativeElement, {
        sitekey: this.turnstileSiteKey,
        action: 'signup',
        callback: (token: string) => {
          this.ngZone.run(() => {
            this.turnstileToken = token || '';
            this.turnstileError = false;
            this.turnstileBlocked = false;
          });
        },
        'expired-callback': () => {
          this.ngZone.run(() => { this.turnstileToken = ''; });
        },
        'error-callback': () => {
          this.ngZone.run(() => {
            this.turnstileToken = '';
            this.turnstileError = true;
          });
        }
      });
    } catch {
      this.ngZone.run(() => { this.turnstileBlocked = true; });
    }
  }

  private detectCountry(): void {
    // Best-effort. Falls back silently — the user can pick manually.
    this.http.getRequest<any>('Account/GetCountryFromIp').subscribe(
      (res) => {
        const name = res?.data?.country;
        if (name && this.countryList.find(c => c.name === name)) {
          this.form.patchValue({ country: name });
        }
      },
      () => { /* ignore */ }
    );
  }

  signUpWithXero(): void {
    this.analytics.capture('signup_sso_clicked', { provider: 'xero' });
    window.location.replace(this.configService.xeroOAuth2SignUpLink);
  }

  // SD-1137: Google / Microsoft / QuickBooks SSO are scaffolded but not yet
  // wired. Xero is the only functional SSO today. When each provider lands,
  // replace the method body with the OAuth redirect — keep the analytics
  // event so the funnel attribution is consistent.
  ssoComingSoon = '';
  private ssoStub(provider: 'google' | 'microsoft' | 'qbo'): void {
    this.analytics.capture('signup_sso_clicked', { provider, stubbed: true });
    this.ssoComingSoon = provider;
    setTimeout(() => { if (this.ssoComingSoon === provider) this.ssoComingSoon = ''; }, 3000);
  }
  signUpWithGoogle():    void { this.ssoStub('google'); }
  signUpWithMicrosoft(): void { this.ssoStub('microsoft'); }
  signUpWithQbo():       void { this.ssoStub('qbo'); }

  ngOnDestroy(): void {
    this.clearPhaseTimer();
    if (this.turnstileRenderTimer) {
      clearTimeout(this.turnstileRenderTimer);
      this.turnstileRenderTimer = null;
    }
    if (this.turnstileWidgetId !== undefined) {
      try { window.turnstile?.remove(this.turnstileWidgetId); } catch { /* noop */ }
      this.turnstileWidgetId = undefined;
    }
  }

  private startLoaderPhases(): void {
    this.currentPhase = 0;
    // Each phase gets ~1.8s. Last phase holds until backend responds.
    this.phaseTimer = setInterval(() => {
      if (this.currentPhase < this.loadingPhases.length - 1) {
        this.currentPhase++;
      } else if (this.backendDone) {
        this.finishLoader();
      }
    }, 1800);
  }

  private clearPhaseTimer(): void {
    if (this.phaseTimer) {
      clearInterval(this.phaseTimer);
      this.phaseTimer = null;
    }
  }

  private finishLoader(): void {
    this.clearPhaseTimer();
    this.currentPhase = this.loadingPhases.length; // all ✓
    // Tiny beat so the user sees everything checked off before navigation.
    setTimeout(() => {
      if (this.pendingNavigate) this.pendingNavigate();
    }, 350);
  }

  // Called once the backend + signIn are both done. Accelerates any remaining phases
  // so the user doesn't wait on our artificial timer after the real work finished.
  private onBackendDone(navigate: () => void): void {
    this.backendDone = true;
    this.pendingNavigate = navigate;
    this.clearPhaseTimer();
    // Fast-flip remaining phases (~250ms each) then navigate.
    const tick = () => {
      if (this.currentPhase < this.loadingPhases.length - 1) {
        this.currentPhase++;
        setTimeout(tick, 250);
      } else {
        this.finishLoader();
      }
    };
    tick();
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.isWarningShown = true;
      return;
    }

    // SD-1165: Backend rejects empty tokens (fail-closed). If the widget never
    // loaded, surface a "blocked" message rather than telling the user to
    // complete a challenge that isn't on screen.
    if (!this.turnstileToken) {
      if (!this.turnstileBlocked) { this.turnstileError = true; }
      this.isWarningShown = true;
      return;
    }

    this.loading = true;
    this.backendDone = false;
    this.startLoaderPhases();
    const v = this.form.value;
    const country = this.countryList.find(c => c.name === v.country);

    const signUpFullModel = {
      email: v.email.toLowerCase(),
      password: v.password,
      firstname: v.firstname,
      lastname: v.lastname,
      companyname: v.companyname,
      name: `${v.firstname} ${v.lastname}`,
      country: v.country,
      accountRemember: v.accountRemember,
      signupMethod: 'email'
    };
    sessionStorage.setItem('newuser', JSON.stringify(signUpFullModel));
    localStorage.setItem('newuser', JSON.stringify(signUpFullModel));

    const signupModel = {
      domain: '',
      user: signUpFullModel,
      organisationIds: '',
      invitedUsers: null,
      organisationId: '',
      country: country?.code.toUpperCase(),
      defaultCurrency: country?.defaultCurrency.toUpperCase(),
      // SD-1165 Cloudflare Turnstile token. Backend rejects empty / invalid.
      cfTurnstileResponse: this.turnstileToken
    };

    this.registerService.signUpNew('Account/CreateProfile', signupModel).subscribe(
      (response) => {
        if (response.status === 0) {
          const newOrganizationID = response.otherData.organizationID;
          const loginModel = {
            email: signUpFullModel.email,
            password: signUpFullModel.password,
            accountRemember: true
          };
          this.authService.signIn(loginModel).pipe(take(1)).subscribe(
            (r) => {
              if (r.status === 0) {
                this.setUser(r, newOrganizationID, signUpFullModel.companyname);
                this.notifications.start();
                this.analytics.capture('account_created', {
                  signup_method: 'email',
                  country: v.country,
                  organization_id: newOrganizationID
                });
                ManageIntercom.identifyUser(true, this.configService);
                // SD-1137: run the loader through its remaining phases before navigating
                // so the user sees the checklist complete instead of a jarring cut.
                this.onBackendDone(() => {
                  this.router.navigate(['/dashboard'], {
                    replaceUrl: true,
                    queryParams: { OrganisationId: newOrganizationID, Domain: 'None' }
                  });
                });
              } else {
                this.bailLoader();
              }
            },
            () => { this.bailLoader(); }
          );
        } else {
          this.bailLoader();
        }
      },
      () => { this.bailLoader(); }
    );
  }

  private bailLoader(): void {
    this.clearPhaseTimer();
    this.loading = false;
    this.backendDone = false;
    this.currentPhase = 0;
    this.pendingNavigate = null;
    // SD-1165: Turnstile tokens are single-use; reset so the user can retry.
    this.turnstileToken = '';
    if (this.turnstileWidgetId !== undefined) {
      try { window.turnstile?.reset(this.turnstileWidgetId); } catch { /* widget not ready yet */ }
    }
  }

  private setUser(response: any, orgId: string, companyName: string): void {
    const u = response.data.user;
    sessionStorage.UserName = u.name;
    sessionStorage.Id = u.id;
    sessionStorage.UserId = u.userId;
    localStorage.setItem('UserName', u.name);
    localStorage.setItem('Email', u.email);
    localStorage.setItem('UserId', u.userId);
    localStorage.setItem('Id', u.id);
    localStorage.setItem('roleId', u.role);
    // Through the service, not straight into localStorage: the setter also tells everything that
    // follows the selected organisation. Written directly, those kept whatever organisation this
    // browser last had open, and the new account's first requests went there and were refused.
    this.organizationService.selectedOrganization = orgId;
    localStorage.setItem('NewOrganizationId', orgId);
    localStorage.setItem('CreatedAt', u.created_date);
    localStorage.setItem('SelectedOrganizationName', companyName);
    // SD-1137: banner reads this flag; backend has not yet confirmed email at signup time.
    localStorage.setItem('EmailConfirmed', String(!!u.emailConfirmed));
    // SD-1137: OnboardingResumeGuard reads this — fresh signup is always step 0.
    localStorage.setItem('OnboardingStep', String(u.onboardingStep ?? 0));
    this.userService.upUserDetails = JSON.stringify(u);
    this.userService.setUserRoleData(u.role);
    sessionStorage.organizationId = orgId;
    sessionStorage.selectedOrganization = orgId;
  }
}